import React, { useState } from 'react';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { Modal } from 'react-responsive-modal';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import ButtonGeneral from '../../components/general/buttons/buttonGeneral';
import DropZoneField from '../../components/general/fields/dropzoneField';
import ModalLoading from '../../components/general/loading/modalLoading';
import { MasterMessage } from '../../../masters/masterMessages';
import {
    asyncPostIvrTypificationBase,
    formatIvrTypificationBaseImportErrorPayload,
    IvrTypificationImportHttpError,
} from '../../../services/ivrTypificationBase.service';
import FieldSelect from '../../components/general/fields/fieldSelect';
import { TYPIFICATION_TYPE_OPTIONS } from './constants';

type Props = {
    open: boolean;
    onClose: (value: boolean) => void;
    saveLoad: (value: boolean) => void;
};

const ModalIvrTypificationBase = ({ open, onClose, saveLoad }: Props) => {
    const [, setDropzoneFormDataList] = useState<any[]>([]);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [successLoad, setSuccessLoad] = useState(false);
    const [typificationType, setTypificationType] = useState('');
    const {
        titleNew,
        titleModalLoading,
        messageModalLoading,
        messageError,
        importInfoNotice,
        importSuccessDetail,
    } = MasterMessage.ivrTypificationBase;

    const canSubmit =
        Boolean(typificationType) && Boolean(uploadFile);

    const handleClick = async () => {
        if (!typificationType) {
            window.alert('Debes seleccionar tipo de tipificación');
            return;
        }

        if (!uploadFile) {
            window.alert('Debes seleccionar algún archivo');
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append('typificationType', typificationType);
        formData.append(
            'file',
            uploadFile,
            uploadFile.name || 'carga.xlsx'
        );

        try {
            const body: any = await asyncPostIvrTypificationBase(formData);
            if (body?.items !== undefined) {
                saveLoad(true);
                setSuccessLoad(true);
            } else {
                const text =
                    formatIvrTypificationBaseImportErrorPayload(body) ||
                    messageError;
                window.alert(text);
            }
        } catch (err: unknown) {
            const httpErr = err as IvrTypificationImportHttpError;
            if (httpErr?.status === 401) {
                return;
            }
            const text = httpErr?.body
                ? formatIvrTypificationBaseImportErrorPayload(httpErr.body)
                : messageError;
            window.alert(text || messageError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                onClose={() => onClose(false)}
                closeOnOverlayClick={false}
                styles={{ modal: { maxWidth: '500px', minWidth: '40%' } }}
            >
                <Box>
                    <Typography
                        color="primary"
                        component="h3"
                        sx={{
                            fontSize: { lg: 27, xs: 23 },
                            mt: { xs: 1.5, sm: 1.5, md: 1.5, lg: 0 },
                        }}
                    >
                        {titleNew}
                    </Typography>
                    <hr />
                    <Box mt={3} px={1}>
                        {!successLoad && (
                            <Alert
                                severity="info"
                                variant="outlined"
                                sx={{ mb: 2, fontSize: 13, lineHeight: 1.5 }}
                            >
                                {importInfoNotice}
                            </Alert>
                        )}
                        <Grid container spacing={{ xs: 2, md: 2 }}>
                            {!successLoad && (
                                <>
                                    <Grid item xs={12}>
                                        <FieldSelect
                                            data={TYPIFICATION_TYPE_OPTIONS}
                                            label="Tipo tipificación"
                                            name="typificationType"
                                            size="small"
                                            value={typificationType}
                                            onChange={(value: any) =>
                                                setTypificationType(
                                                    value?.target?.value || ''
                                                )
                                            }
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12}>
                                {successLoad ? (
                                    <Box>
                                        <Typography
                                            component="p"
                                            sx={{ fontWeight: 600, mb: 1 }}
                                        >
                                            Importación completada
                                        </Typography>
                                        <Typography
                                            component="p"
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.6 }}
                                        >
                                            {importSuccessDetail}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <DropZoneField
                                        name="Cargue tipificaciones IVR"
                                        maxFiles={1}
                                        accept=".xlsx"
                                        onChange={setDropzoneFormDataList}
                                        onFileSelected={(file) =>
                                            setUploadFile(file)
                                        }
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} sx={{ textAlign: 'right' }}>
                                {!successLoad ? (
                                    <>
                                        <ButtonGeneral
                                            onClick={() => handleClick()}
                                            startIcon={
                                                <FileUploadIcon color="secondary" />
                                            }
                                            title="Cargar"
                                            disabled={!canSubmit}
                                            sx={{ mr: 1 }}
                                        />
                                        <ButtonGeneral
                                            onClick={() => onClose(false)}
                                            startIcon={
                                                <CancelIcon color="secondary" />
                                            }
                                            title="Cancelar"
                                        />
                                    </>
                                ) : (
                                    <ButtonGeneral
                                        onClick={() => onClose(false)}
                                        startIcon={
                                            <CheckIcon color="secondary" />
                                        }
                                        title="Aceptar"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Modal>
            {loading && (
                <ModalLoading
                    open={loading}
                    title={titleModalLoading}
                    message={messageModalLoading}
                />
            )}
        </>
    );
};

export default ModalIvrTypificationBase;
